import * as React from 'react';

import { Input, InputProps } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';

/**
 * PhoneInput — the canonical phone-number text input.
 *
 * Phone numbers are intrinsically left-to-right (the leading `+`, country code
 * and digit grouping read LTR in every locale). This wrapper forces `dir="ltr"`
 * so the typed value AND the placeholder hint render left-to-right even on an
 * RTL (e.g. Hebrew) page, and left-aligns the content so it isn't pushed to the
 * right edge of the field. It also sets `type="tel"` / `inputMode="tel"` so
 * mobile keyboards show the numeric phone pad.
 *
 * Use this anywhere a phone number is entered. For react-hook-form +
 * validation, prefer `ValidatedPhoneInput`; for inline-edit detail pages, pass
 * `dir="ltr"` to `InlineEditableField`.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      autoComplete="tel"
      {...props}
      // LTR + tel semantics are non-negotiable for phone numbers — keep them
      // after the spread so a caller can't accidentally override them.
      type="tel"
      inputMode="tel"
      dir="ltr"
      className={cn('text-left', className)}
    />
  )
);
PhoneInput.displayName = 'PhoneInput';
